import React, { useEffect, useState } from "react";
import "../JobPosting/jobs.scss";
import "./candidates.scss";
import AddCandidatesModal from "./AddCandidatesModal";
import { useSelector, useDispatch } from "react-redux";
import {
    getFolders,
    getListsByFolder,
    getListCandidates,
    clearListCandidates,
} from "../../Redux/ActionCreators/candidateAction";
import { downloadResume } from "./candidateHelpers";
import AllCandidatesTab from "./AllCandidatesTab";
import ListTab from "./ListTab";
import SmartSearchTab from "./SmartSearchTab";
import ResumeSummaryPopup from "./ResumeSummaryPopup";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";

const CandidateManagement = () => {
    const dispatch = useDispatch();
    const { can } = useAppPermission();
    const canCreateCandidate = can(P.CANDIDATES_CANDIDATE_CREATE);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [activeTab, setActiveTab] = useState("Smart Search");
    const [allCandidatesLoading, setAllCandidatesLoading] = useState(false);

    // Shared folder/list state (used by both All Candidates and List tabs)
    const [selectedFolder, setSelectedFolder] = useState("");
    const [folderLoading, setFolderLoading] = useState(false);
    const [folderOptions, setFolderOptions] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listOptions, setListOptions] = useState([]);

    // All Candidates tab: currently selected list filter
    const [allCandidatesListOptions, setAllCandidatesListOptions] = useState([]);
    const [allCandidatesListLoading, setAllCandidatesListLoading] = useState(false);
    const [allCandidatesFolderOptions, setAllCandidatesFolderOptions] = useState([]);
    const [allCandidatesFolderLoading, setAllCandidatesFolderLoading] = useState(false);
    const [allCandidatesCurrentFolderFilter, setAllCandidatesCurrentFolderFilter] = useState(undefined);

    const [selectedListView, setSelectedListView] = useState(null);
    const [listViewCandidates, setListViewCandidates] = useState([]);
    const [listViewLoading, setListViewLoading] = useState(false);
    const [listViewPagination, setListViewPagination] = useState(null);

    const [showResumeSummaryPopup, setShowResumeSummaryPopup] = useState(false);
    const [selectedCandidateForPopup, setSelectedCandidateForPopup] = useState(null);
    const [allCandidatesPagination, setAllCandidatesPagination] = useState(null);

    const listCandidates = useSelector(state => state.candidates?.listCandidates || []);

    // Load folders for the All Candidates tab filters on mount / tab switch
    useEffect(() => {
        if (activeTab === "All Candidates") {
            setAllCandidatesLoading(true);
            dispatch(getListCandidates(undefined, undefined, undefined, 10, 1)).then((res) => {
                setAllCandidatesLoading(false);
                if (res?.pagination) setAllCandidatesPagination(res.pagination);
            });
            // Fetch folders for filter dropdowns
            setAllCandidatesFolderLoading(true);
            dispatch(getFolders()).then((res) => {
                setAllCandidatesFolderLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setAllCandidatesFolderOptions(res.data.map((f) => ({ value: f.id, label: f.name })));
                } else {
                    setAllCandidatesFolderOptions([]);
                }
            });
            // Load all lists (no folder filter) for list dropdown
            setAllCandidatesListLoading(true);
            dispatch(getListsByFolder("")).then((res) => {
                setAllCandidatesListLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setAllCandidatesListOptions(res.data);
                } else {
                    setAllCandidatesListOptions([]);
                }
            });
        } else if (activeTab === "List") {
            dispatch(clearListCandidates());
            setSelectedListView(null);
            setListViewCandidates([]);
            setSelectedFolder("");
            setFolderLoading(true);
            dispatch(getFolders()).then((res) => {
                setFolderLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setFolderOptions(res.data.map((f) => ({ value: f.id, label: f.name })));
                } else {
                    setFolderOptions([]);
                }
            });
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== "List") {
            setListOptions([]);
            return;
        }
        setListLoading(true);
        dispatch(getListsByFolder(selectedFolder || "")).then((res) => {
            setListLoading(false);
            if (res?.success && Array.isArray(res.data)) {
                setListOptions(res.data);
            } else {
                setListOptions([]);
            }
        });
    }, [selectedFolder, activeTab]);

    const handleListSelect = async (listItem) => {
        setSelectedListView(listItem);
        setListViewLoading(true);
        setListViewCandidates([]);
        setListViewPagination(null);
        const res = await dispatch(getListCandidates(listItem.folder?.id, listItem.id, undefined, 10, 1));
        setListViewLoading(false);
        if (res?.success && Array.isArray(res.data)) {
            setListViewCandidates(res.data);
            if (res?.pagination) setListViewPagination(res.pagination);
        } else {
            setListViewCandidates([]);
        }
    };

    const handleListViewPageChange = ({ limit = 10, page = 1, search = undefined }) => {
        if (!selectedListView) return;
        setListViewLoading(true);
        dispatch(getListCandidates(selectedListView.folder?.id, selectedListView.id, search || undefined, limit, page))
            .then((res) => {
                setListViewLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setListViewCandidates(res.data);
                    if (res?.pagination) setListViewPagination(res.pagination);
                } else {
                    setListViewCandidates([]);
                }
            });
    };

    const handleBackToList = () => {
        setSelectedListView(null);
        setListViewCandidates([]);
        setListViewPagination(null);
    };

    const handleSearchLists = ({ name, folder_id }) => {
        setListLoading(true);
        dispatch(getListsByFolder(folder_id || "", name)).then((res) => {
            setListLoading(false);
            if (res?.success && Array.isArray(res.data)) {
                setListOptions(res.data);
            } else {
                setListOptions([]);
            }
        });
    };

    // Called from AllCandidatesTab whenever search/folder/list filter or page/limit changes
    const handleAllCandidatesFilter = ({ search, folder_id, list_id, limit = 10, page = 1 }) => {
        setAllCandidatesLoading(true);
        dispatch(getListCandidates(folder_id || undefined, list_id || undefined, search || undefined, limit, page))
            .then((res) => {
                setAllCandidatesLoading(false);
                if (res?.pagination) setAllCandidatesPagination(res.pagination);
            });

        // Only reload list options when the folder filter actually changes
        if (folder_id !== allCandidatesCurrentFolderFilter) {
            setAllCandidatesCurrentFolderFilter(folder_id);
            setAllCandidatesListLoading(true);
            dispatch(getListsByFolder(folder_id || "")).then((res) => {
                setAllCandidatesListLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setAllCandidatesListOptions(res.data);
                } else {
                    setAllCandidatesListOptions([]);
                }
            });
        }
    };

    const handleViewSummary = (c) => {
        setSelectedCandidateForPopup(c);
        setShowResumeSummaryPopup(true);
    };

    const handleClosePopup = () => {
        setShowResumeSummaryPopup(false);
        setSelectedCandidateForPopup(null);
    };

    const handleUpload = (files) => {
        console.log("Files to upload:", files);
    };

    return (
        <div className="job-table-container cv-fill-page" style={{margin:'0 12px'}}>
            {selectedListView ? (
                <div className="top-navigation">
                    <div className="createjob-tab">
                        <div className="d-flex align-items-center gap-2">
                            <img
                                src="/images/leftarrow-black-icon.svg"
                                alt="Back"
                                width={30}
                                height={30}
                                style={{ cursor: "pointer" }}
                                onClick={handleBackToList}
                            />
                            <h4 style={{ margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>
                                <span className="cursor-pointer" onClick={handleBackToList}>List</span>
                                <span>/</span>
                                <span>{selectedListView.name}</span>
                            </h4>
                        </div>
                        {canCreateCandidate && (
                        <button
                            className="create-job"
                            onClick={() => setShowUploadModal(true)}
                            style={{ width: "unset" }}
                        >
                            Add Candidates
                        </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="top-navigation">
                    <div className="createjob-tab">
                        <h4>Candidates</h4>
                        {canCreateCandidate && (
                        <button
                            className="create-job"
                            onClick={() => setShowUploadModal(true)}
                            style={{ width: "unset" }}
                        >
                            Add Candidates
                        </button>
                        )}
                    </div>
                    <div className="tab-navigation">
                        {["Smart Search", "All Candidates", "List"].map((tab) => (
                            <button
                                key={tab}
                                className={`tab-item ${activeTab === tab ? "active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Always mounted so search results persist across tab switches */}
            <div className="cv-smart-search-wrap" style={{ display: activeTab === "Smart Search" ? "flex" : "none" }}>
                <SmartSearchTab
                    onViewSummary={handleViewSummary}
                    onDownload={downloadResume}
                    onAddClick={() => setShowUploadModal(true)}
                />
            </div>

            {activeTab === "All Candidates" && (
                <AllCandidatesTab
                    candidates={listCandidates}
                    loading={allCandidatesLoading}
                    onViewSummary={handleViewSummary}
                    onDownload={downloadResume}
                    onAddClick={() => setShowUploadModal(true)}
                    folderOptions={allCandidatesFolderOptions}
                    folderLoading={allCandidatesFolderLoading}
                    listOptions={allCandidatesListOptions}
                    listLoading={allCandidatesListLoading}
                    onFilterChange={handleAllCandidatesFilter}
                    pagination={allCandidatesPagination}
                />
            )}

            {activeTab === "List" && (
                <ListTab
                    folderOptions={folderOptions}
                    folderLoading={folderLoading}
                    selectedFolder={selectedFolder}
                    onFolderChange={setSelectedFolder}
                    listOptions={listOptions}
                    listLoading={listLoading}
                    selectedListView={selectedListView}
                    listViewCandidates={listViewCandidates}
                    listViewLoading={listViewLoading}
                    listViewPagination={listViewPagination}
                    onListViewPageChange={handleListViewPageChange}
                    onListSelect={handleListSelect}
                    onViewSummary={handleViewSummary}
                    onDownload={downloadResume}
                    onAddClick={() => setShowUploadModal(true)}
                    getListsByFolder={handleSearchLists}
                />
            )}

            <AddCandidatesModal
                show={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onUpload={handleUpload}
                onFolderCreated={() => {
                    // Refetch folders for the parent when a new one is created
                    if (activeTab === "List") {
                        setFolderLoading(true);
                        dispatch(getFolders()).then((res) => {
                            setFolderLoading(false);
                            if (res?.success && Array.isArray(res.data)) {
                                setFolderOptions(res.data.map((f) => ({ value: f.id, label: f.name })));
                            } else {
                                setFolderOptions([]);
                            }
                        });
                    }
                }}
                onListCreated={(folderId) => {
                    // Refetch lists for the parent when a new one is created
                    if (activeTab === "List") {
                        setListLoading(true);
                        dispatch(getListsByFolder(selectedFolder || "")).then((res) => {
                            setListLoading(false);
                            if (res?.success && Array.isArray(res.data)) {
                                setListOptions(res.data);
                            } else {
                                setListOptions([]);
                            }
                        });
                    }
                }}
                preselect={selectedListView ? {
                    folder: selectedListView.folder
                        ? { value: selectedListView.folder.id, label: selectedListView.folder.name }
                        : null,
                    list: { value: selectedListView.id, label: selectedListView.name },
                } : null}
            />

            {showResumeSummaryPopup && (
                <ResumeSummaryPopup
                    candidate={selectedCandidateForPopup}
                    onClose={handleClosePopup}
                />
            )}
        </div>
    );
};

export default CandidateManagement;